import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X, ArrowRight, FileText, Mail, User, Briefcase, Map, Cpu, Terminal, Sparkles } from 'lucide-react'
import { COMMAND } from '../constants/testIds'

const navCommands = [
  { id: 'hero', label: 'Go to Home', icon: Terminal, section: true },
  { id: 'about', label: 'About Akshay', icon: User, section: true },
  { id: 'timeline', label: 'Career Journey', icon: Map, section: true },
  { id: 'work', label: 'Case Studies', icon: Briefcase, section: true },
  { id: 'services', label: 'Services & Pricing', icon: Sparkles, section: true },
  { id: 'skills', label: 'Skills & Capabilities', icon: Cpu, section: true },
  { id: 'contact', label: 'Get in Touch', icon: Mail, section: true },
]

const actionCommands = [
  { id: 'resume', label: 'View Resume', icon: FileText, action: () => window.location.href = '/resume' },
  { id: 'github', label: 'View GitHub', icon: Terminal, action: () => window.open('https://github.com/akshayrao1', '_blank') },
  { id: 'email', label: 'Send Email', icon: Mail, action: () => window.open('mailto:akshayraoisme@gmail.com', '_blank') },
]

export default function CommandPalette({ open, onClose, onNavigate }) {
  const [query, setQuery] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) { setQuery(''); setTimeout(() => inputRef.current?.focus(), 50) }
  }, [open])

  const filtered = [...navCommands, ...actionCommands].filter(cmd =>
    !query || cmd.label.toLowerCase().includes(query.toLowerCase())
  )

  const handleSelect = (cmd) => {
    if (cmd.section) onNavigate(cmd.id)
    else if (cmd.action) cmd.action()
    onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[100] bg-black/20 backdrop-blur-sm"
            onClick={onClose} aria-hidden="true" />

          <motion.div data-testid={COMMAND.root}
            initial={{ opacity: 0, scale: 0.97, y: -16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -16 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-[18%] left-1/2 -translate-x-1/2 z-[101] w-full max-w-lg mx-auto px-4"
            role="dialog" aria-label="Command palette"
          >
            <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-float overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#F3F4F6]">
                <Search size={14} className="text-[#9CA3AF] shrink-0" />
                <input ref={inputRef} data-testid={COMMAND.input} type="text" value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search or navigate..."
                  className="flex-1 bg-transparent text-[#111111] text-sm font-body placeholder:text-[#D1D5DB] focus:outline-none" />
                <button data-testid={COMMAND.closeBtn} onClick={onClose} aria-label="Close palette"
                  className="text-[#9CA3AF] hover:text-[#374151] transition-colors p-1">
                  <X size={13} />
                </button>
              </div>

              <div className="py-2 max-h-80 overflow-y-auto">
                {!query && (
                  <div className="px-4 py-2 font-mono text-[10px] text-[#9CA3AF] uppercase tracking-[0.2em]">Navigation</div>
                )}
                {filtered.length === 0 && (
                  <div className="px-4 py-6 text-center font-body text-sm text-[#9CA3AF]">No results for "{query}"</div>
                )}
                {filtered.map((cmd) => <CommandItem key={cmd.id} cmd={cmd} onSelect={() => handleSelect(cmd)} />)}
              </div>

              <div className="px-4 py-2.5 border-t border-[#F3F4F6] flex items-center gap-4 text-[11px] font-mono text-[#9CA3AF]">
                <span>↑↓ navigate</span>
                <span>↵ select</span>
                <span>esc close</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function CommandItem({ cmd, onSelect }) {
  const Icon = cmd.icon
  return (
    <button onClick={onSelect}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#F3F4F6] group transition-colors duration-100 text-left">
      <div className="p-1.5 border border-[#E5E7EB] rounded-lg bg-[#FAFAFA] group-hover:border-[#0047AB]/25 group-hover:bg-[#EBF2FF] transition-all duration-150">
        <Icon size={12} className="text-[#9CA3AF] group-hover:text-[#0047AB] transition-colors" />
      </div>
      <span className="font-body text-sm text-[#374151] group-hover:text-[#111111] transition-colors flex-1">{cmd.label}</span>
      <ArrowRight size={10} className="text-[#D1D5DB] group-hover:text-[#0047AB] transition-colors opacity-0 group-hover:opacity-100" />
    </button>
  )
}
